import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import { useFormatCurrency } from '../config/useAppConfig';
import { useProperties } from '../hooks/useProperties';
import { useAuth } from '../contexts/AuthContext';


const ListingCard = ({
  image,
  title,
  price,
  location,
  status,
  statLabel,
  statValue,
  reviewLabel,
  reviewValue,
  isDraft = false,
  id,
  onDelete,
  onClick,
  formatCurrency,
  canEdit = true,
  canDelete = true,
}) => (
  <div 
    className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
    onClick={() => onClick({ image, title, price, location, status, statLabel, statValue, reviewLabel, reviewValue, isDraft, id })}
  >
    <div className={`relative h-56 overflow-hidden ${isDraft ? 'grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100' : ''}`}>
      <img 
        src={image || 'https://via.placeholder.com/400x300?text=No+Image'} 
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
        alt={title} 
      />
      <div className="absolute top-4 left-4">
        <span className={`px-3 py-1 ${isDraft ? 'bg-muted-foreground' : 'bg-tertiary/90'} text-white text-[10px] font-black rounded-full uppercase backdrop-blur-md shadow-lg`}>
          {status}
        </span>
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    </div>
    <div className="p-6">
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-lg font-heading font-bold group-hover:text-primary transition-colors">{title}</h3>
        <span className="text-primary font-black">{price}</span>
      </div>
      <p className="text-xs text-muted-foreground flex items-center gap-1 mb-4">
        <Icon icon="lucide:map-pin" className="text-primary" />
        {location}
      </p>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-muted/50 p-3 rounded-xl text-center border border-transparent hover:border-primary/20 transition-colors">
          <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{statLabel}</p>
          <p className="text-sm font-bold">{statValue}</p>
        </div>
        <div className="bg-muted/50 p-3 rounded-xl text-center border border-transparent hover:border-primary/20 transition-colors">
          <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{reviewLabel}</p>
          <p className="text-sm font-bold">{reviewValue}</p>
        </div>
      </div>
      <div className="flex gap-2">
        {canEdit && (
          <Link to={`/listings/edit/${id}`} className="flex-1 py-3 bg-primary text-primary-foreground text-xs font-black rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all active:scale-95 uppercase tracking-widest text-center">
            {isDraft ? 'Finish Setup' : 'Edit Details'}
          </Link>
        )}
        {canDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(id);
            }}
            className="px-4 py-3 bg-muted text-foreground text-xs font-black rounded-xl hover:bg-muted/80 transition-all active:scale-95 uppercase tracking-widest"
          >
            <Icon icon="lucide:trash-2" className="text-lg" />
          </button>
        )}
      </div>
    </div>
  </div>
);

export default function Listings() {
  const formatCurrency = useFormatCurrency();
  const { properties, loading, error, deleteProperty } = useProperties();
  const { hasPermission } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedListing, setSelectedListing] = useState(null);

  // Filter listings based on search query
  const filteredListings = properties.filter(listing =>
    (listing.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (listing.address || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (listing.type || '').toLowerCase().includes(searchQuery.toLowerCase())
  );


  const handleListingClick = (listingData) => {
    const fullListing = properties.find(listing => listing.id === listingData.id);
    setSelectedListing(fullListing || listingData);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this listing?')) {
      await deleteProperty(id);
    }
  };
  
  return (
    <PageLayout>
      <>
        <PageHeader
          title="Manage Listings"
          description="Add, edit, and optimize your property portfolio."
          actions={[
            ...(hasPermission('addProperty') ? [{
              type: 'link',
              label: 'Add New Listing',
              shortLabel: 'Add Listing',
              icon: 'lucide:plus',
              to: '/listings/add-new',
              variant: 'primary'
            }] : []),
          ]}
        />

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-8 scroll-smooth">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Icon icon="lucide:search" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search listings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-muted border border-transparent rounded-xl text-sm focus:bg-card focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all outline-none"
              />
            </div>
            <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto no-scrollbar">
              <select className="bg-muted border border-transparent rounded-xl text-xs px-4 py-2 font-bold outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all cursor-pointer min-w-[120px]">
                <option>All Locations</option>
              </select>
              <select className="bg-muted border border-transparent rounded-xl text-xs px-4 py-2 font-bold outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all cursor-pointer min-w-[120px]">
                <option>Status: All</option>
                <option>Active</option>
                <option>Draft</option>
                <option>Inactive</option>
              </select>
              <button className="p-2.5 bg-muted rounded-xl hover:bg-primary/10 hover:text-primary transition-all active:scale-95 shrink-0">
                <Icon icon="lucide:funnel" className="text-xl" />
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-destructive/10 text-destructive p-4 rounded-xl text-sm font-bold">
              {error}
            </div>
          )}

          {/* Listings Grid */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Icon icon="lucide:loader-2" className="text-4xl text-primary animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {filteredListings.map((listing) => {
                const images = typeof listing.images === 'string' ? JSON.parse(listing.images) : (listing.images || []);
                return (
                  <ListingCard
                    key={listing.id}
                    image={images[0]}
                    title={listing.name}
                    price={`${formatCurrency(listing.pricePerNight)}/night`}
                    location={listing.address}
                    status={listing.status === 'available' ? 'Active' : 'Maintenance'}
                    statLabel="Guests"
                    statValue={listing.maxGuests}
                    reviewLabel="Rooms"
                    reviewValue={`${listing.bedrooms} BR / ${listing.bathrooms} BA`}
                    id={listing.id}
                    isDraft={listing.status === 'maintenance'}
                    onDelete={handleDelete}
                    onClick={handleListingClick}
                    formatCurrency={formatCurrency}
                    canEdit={hasPermission('editProperty')}
                    canDelete={hasPermission('deleteProperty')}
                  />
                );
              })}
            </div>
          )}
        </div>
      </>
    </PageLayout>
  );
}

